/**
 * App router — the thin top of the renderer.
 *
 *  - Subscribes to the Store and re-renders the correct screen whenever state
 *    changes (run phase or UI overlay).
 *  - Maps (overlay, run.phase) -> a screen module. Overlays (title/deck/menu/
 *    settings/compendium/results) win over the run phase; otherwise the run's
 *    phase drives the screen (map/combat/reward/shop/event/rest).
 *  - Owns global keyboard routing (delegating to per-screen handlers) and the
 *    responsive scale transform that fits the fixed 960x600 canvas to any window.
 */

import { mount, div } from "./dom";
import type { Store } from "./store";

import { renderTitle, resetTitleSetup } from "./screens/title";
import { renderMap, mapKey, resetMapFocus } from "./screens/map";
import { renderCombat, combatKey, resetCombatUi } from "./screens/combat";
import { renderReward, resetRewardUi } from "./screens/reward";
import { renderShop, resetShopUi } from "./screens/shop";
import { renderEvent, resetEventUi } from "./screens/event";
import { renderRest, restKey } from "./screens/rest";
import { renderDeck, resetDeckTab } from "./screens/deck";
import { renderResults } from "./screens/results";
import { renderCompendium } from "./screens/compendium";
import { renderSettings } from "./screens/settings";
import { renderMapMenu } from "./screens/menu";

const APP_W = 960;
const APP_H = 600;

export class App {
  private root: HTMLElement;
  private store: Store;
  /** Tracks the last-rendered "scene key" so we can reset per-screen UI state. */
  private lastScene = "";

  constructor(root: HTMLElement, store: Store) {
    this.root = root;
    this.store = store;
    store.subscribe(() => this.render());
    window.addEventListener("keydown", (e) => this.onKey(e));
    window.addEventListener("resize", () => this.fit());
    this.fit();
    this.render();
  }

  /** Scale the fixed canvas to fit the window while preserving aspect ratio. */
  private fit(): void {
    const margin = 24;
    const sw = (window.innerWidth - margin) / APP_W;
    const sh = (window.innerHeight - margin) / APP_H;
    const scale = Math.max(0.4, Math.min(sw, sh));
    this.root.style.transform = `scale(${scale})`;
  }

  /** Compute the active scene key (overlay or phase). */
  private sceneKey(): string {
    const s = this.store;
    if (s.overlay !== "none") return `ov:${s.overlay}`;
    if (!s.run) return "title";
    return `ph:${s.run.phase}`;
  }

  render(): void {
    const s = this.store;

    // Reduced-motion class on the frame.
    document.body.classList.toggle(
      "reduced-motion",
      s.meta.settings.reducedMotion,
    );

    // Reset per-screen transient UI when the scene changes (so stale selection/
    // focus/tabs never carry across screens).
    const scene = this.sceneKey();
    if (scene !== this.lastScene) {
      this.resetTransient(scene);
      this.lastScene = scene;
    }

    mount(this.root, this.screen());

    // Surface a transient notice (save error etc.) as a strip, then clear.
    if (s.notice) {
      const strip = div("notice err", [s.notice]);
      strip.style.position = "absolute";
      strip.style.left = "8px";
      strip.style.right = "8px";
      strip.style.bottom = "8px";
      strip.style.zIndex = "200";
      strip.addEventListener("click", () => s.clearNotice());
      this.root.appendChild(strip);
      // Auto-dismiss.
      setTimeout(() => s.clearNotice(), 4000);
    }
  }

  /** Pick + build the active screen node. */
  private screen(): HTMLElement {
    const s = this.store;

    // Overlays take precedence.
    switch (s.overlay) {
      case "title":
        return renderTitle(s);
      case "deck":
        return renderDeck(s);
      case "settings":
        return renderSettings(s);
      case "compendium":
        return renderCompendium(s);
      case "results":
        return renderResults(s);
      case "map-menu":
        return renderMapMenu(s);
      case "none":
      default:
        break;
    }

    // No run -> title.
    if (!s.run) return renderTitle(s);

    // Run phase -> screen.
    switch (s.run.phase) {
      case "map":
        return renderMap(s);
      case "combat":
        return renderCombat(s);
      case "reward":
        return renderReward(s);
      case "shop":
        return renderShop(s);
      case "event":
        return renderEvent(s);
      case "rest":
        return renderRest(s);
      case "dead":
      case "victory":
        return renderResults(s);
      default:
        return renderTitle(s);
    }
  }

  /** Reset module-local UI state appropriate to the new scene. */
  private resetTransient(scene: string): void {
    if (scene === "title" || scene === "ov:title") resetTitleSetup();
    if (scene === "ph:map") resetMapFocus();
    if (scene === "ph:combat") resetCombatUi();
    if (scene === "ph:reward") resetRewardUi();
    if (scene === "ph:shop") resetShopUi();
    if (scene === "ph:event") resetEventUi();
    if (scene === "ov:deck") resetDeckTab();
  }

  /** Global keyboard router: overlays first, then per-phase handlers. */
  private onKey(e: KeyboardEvent): void {
    const s = this.store;
    // Ignore when typing into an input (seed/import fields).
    const tag = (e.target as HTMLElement)?.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA") return;

    const key = e.key;

    // Overlay-level Esc closes the overlay (deck/menu/settings/compendium).
    if (s.overlay !== "none" && s.overlay !== "title" && s.overlay !== "results") {
      if (key === "Escape") {
        e.preventDefault();
        s.closeOverlay();
        return;
      }
      return; // let overlay buttons handle the rest via clicks
    }

    if (!s.run) return;

    // Per-phase keyboard.
    let handled = false;
    if (s.run.phase === "combat") handled = combatKey(s, key);
    else if (s.run.phase === "map") {
      if (key === "Escape") {
        s.open("map-menu");
        handled = true;
      } else handled = mapKey(s, key);
    } else if (s.run.phase === "rest") handled = restKey(s, key);

    if (handled) e.preventDefault();
  }
}
